import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config';
import quranChapters from '../data/quranChapters';
import { formatDateOnly } from '../utils/dateDisplay';
import './QuranAssignmentsModal.css';

const emptyForm = {
  surah_number: '',
  start_verse: '1',
  end_verse: '1',
  due_date: '',
  instructions: '',
};

export default function QuranAssignmentsModal({ show, onHide, student }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const studentId = student?.student_id || student?.id;
  const studentName = [
    student?.student_first_name || student?.first_name,
    student?.student_last_name || student?.last_name,
  ].filter(Boolean).join(' ');
  const selectedChapter = useMemo(
    () => quranChapters.find(chapter => String(chapter.number) === String(form.surah_number)),
    [form.surah_number]
  );

  useEffect(() => {
    if (!show || !studentId) return;
    setForm(emptyForm);
    setLoading(true);
    axios.get(`${API_URL}/students/${studentId}/quran-assignments`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(({ data }) => setAssignments(Array.isArray(data) ? data : []))
      .catch((error) => {
        console.error('Load Quran assignments failed', error);
        toast.error(error?.response?.data?.error || 'Could not load Quran assignments');
      })
      .finally(() => setLoading(false));
  }, [show, studentId]);

  const addAssignment = async (event) => {
    event.preventDefault();
    if (!selectedChapter) return toast.warn('Please select a surah');

    try {
      setSaving(true);
      const { data } = await axios.post(
        `${API_URL}/students/${studentId}/quran-assignments`,
        form,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setAssignments(previous => [data, ...previous]);
      setForm(emptyForm);
      toast.success('Quran assignment added');
    } catch (error) {
      console.error('Add Quran assignment failed', error);
      toast.error(error?.response?.data?.error || 'Failed to add Quran assignment');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (assignment) => {
    const status = assignment.status === 'completed' ? 'assigned' : 'completed';
    try {
      await axios.patch(
        `${API_URL}/students/${studentId}/quran-assignments/${assignment.id}`,
        { status },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setAssignments(previous => previous.map(item => item.id === assignment.id ? { ...item, status } : item));
    } catch (error) {
      console.error('Update Quran assignment failed', error);
      toast.error('Failed to update Quran assignment');
    }
  };

  const deleteAssignment = async (assignment) => {
    if (!window.confirm(`Delete the ${assignment.surah_name} assignment?`)) return;
    try {
      await axios.delete(`${API_URL}/students/${studentId}/quran-assignments/${assignment.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setAssignments(previous => previous.filter(item => item.id !== assignment.id));
      toast.success('Quran assignment deleted');
    } catch (error) {
      console.error('Delete Quran assignment failed', error);
      toast.error('Failed to delete Quran assignment');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" fullscreen="sm-down" dialogClassName="quran-manager-modal">
      <Modal.Header closeButton>
        <Modal.Title>Quran assignments{studentName ? ` — ${studentName}` : ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className="quran-manager-form" onSubmit={addAssignment}>
          <div className="quran-manager-form__intro">
            <div><h3>Assign a passage</h3><p>Select a surah and verse range.</p></div>
            <i className="bi bi-book" aria-hidden="true" />
          </div>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Surah</Form.Label>
                <Form.Select
                  value={form.surah_number}
                  onChange={(event) => setForm(previous => ({ ...previous, surah_number: event.target.value, start_verse: '1', end_verse: '1' }))}
                  required
                >
                  <option value="">Select a surah</option>
                  {quranChapters.map(chapter => (
                    <option key={chapter.number} value={chapter.number}>{chapter.number}. {chapter.name} ({chapter.verseCount} verses)</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} md={3}>
              <Form.Group>
                <Form.Label>From verse</Form.Label>
                <Form.Select
                  value={form.start_verse}
                  disabled={!selectedChapter}
                  onChange={(event) => {
                    const startVerse = event.target.value;
                    setForm(previous => ({
                      ...previous,
                      start_verse: startVerse,
                      end_verse: Number(previous.end_verse) < Number(startVerse) ? startVerse : previous.end_verse,
                    }));
                  }}
                >
                  {Array.from({ length: selectedChapter?.verseCount || 1 }, (_, index) => <option key={index + 1}>{index + 1}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} md={3}>
              <Form.Group>
                <Form.Label>To verse</Form.Label>
                <Form.Select
                  value={form.end_verse}
                  disabled={!selectedChapter}
                  onChange={(event) => setForm(previous => ({ ...previous, end_verse: event.target.value }))}
                >
                  {Array.from({ length: selectedChapter?.verseCount || 1 }, (_, index) => index + 1)
                    .filter(verse => verse >= Number(form.start_verse))
                    .map(verse => <option key={verse}>{verse}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Due date <span className="text-muted">(optional)</span></Form.Label>
                <Form.Control type="date" value={form.due_date} onChange={(event) => setForm(previous => ({ ...previous, due_date: event.target.value }))} />
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group>
                <Form.Label>Instructions <span className="text-muted">(optional)</span></Form.Label>
                <Form.Control
                  value={form.instructions}
                  onChange={(event) => setForm(previous => ({ ...previous, instructions: event.target.value }))}
                  placeholder="For example: memorize and review with tajweed"
                  maxLength={1000}
                />
              </Form.Group>
            </Col>
          </Row>
          <Button type="submit" variant="success" disabled={!selectedChapter || saving}>
            {saving ? 'Assigning…' : 'Assign Quran'}
          </Button>
        </Form>

        <div className="quran-manager-list">
          <div className="quran-manager-list__heading"><h3>Assignments</h3><span>{assignments.length}</span></div>
          {loading ? (
            <div className="quran-manager-empty">Loading assignments…</div>
          ) : assignments.length === 0 ? (
            <div className="quran-manager-empty"><i className="bi bi-book" aria-hidden="true" /><strong>No Quran assignments yet</strong></div>
          ) : assignments.map(assignment => (
            <article className={`quran-manager-card${assignment.status === 'completed' ? ' quran-manager-card--completed' : ''}`} key={assignment.id}>
              <div className="quran-manager-card__content">
                <span>Surah {assignment.surah_number}</span>
                <h4>{assignment.surah_name}</h4>
                <p>Verses <strong>{assignment.start_verse}–{assignment.end_verse}</strong></p>
                {assignment.instructions && <p>{assignment.instructions}</p>}
                <small>{assignment.due_date ? `Due ${formatDateOnly(assignment.due_date)}` : 'No due date'} · Assigned by {assignment.teacher_name || 'Admin'}</small>
              </div>
              <div className="quran-manager-card__actions">
                <span>{assignment.status === 'completed' ? 'Completed' : 'Assigned'}</span>
                <Button size="sm" variant={assignment.status === 'completed' ? 'outline-secondary' : 'success'} onClick={() => toggleStatus(assignment)}>
                  {assignment.status === 'completed' ? 'Reopen' : 'Mark complete'}
                </Button>
                <Button size="sm" variant="outline-danger" onClick={() => deleteAssignment(assignment)}>Delete</Button>
              </div>
            </article>
          ))}
        </div>
      </Modal.Body>
    </Modal>
  );
}
